import { useState } from "react";
import { useContent } from "../App";
import { Leaf, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { usePageSEO } from "../hooks/usePageSEO";

export default function RestaurantPage() {
  const { menu, content } = useContent();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);

  usePageSEO({
    title: "Pure Veg Restaurant Menu | Ambe Palace - Best Hotel in Dungarpur",
    description: "Explore the full pure vegetarian restaurant menu at Ambe Palace, Dungarpur. Indian cuisine, paneer dishes, breads, rice, starters, soups, pizza & desserts. Open 10 AM to 12 AM."
  });

  const restaurant = content.restaurant || {
    title: "Pure Veg Restaurant \u2013 Ambe Palace",
    text: "Our in-house restaurant serves a variety of delicious pure vegetarian dishes prepared with fresh ingredients.",
    hours: "10 AM \u2013 12 AM"
  };

  const filteredMenu = search
    ? menu.map(cat => ({
        ...cat,
        items: cat.items.filter(item =>
          item.name.toLowerCase().includes(search.toLowerCase())
        )
      })).filter(cat => cat.items.length > 0)
    : activeCategory !== null
      ? [menu[activeCategory]]
      : menu;

  return (
    <div data-testid="restaurant-page">
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-green-600/90 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 mb-4">
            <Leaf className="w-3.5 h-3.5" />
            Pure Veg
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Pure Veg Restaurant in Dungarpur {'\u2014'} Ambe Palace
          </h1>
          <p className="text-base text-slate-400 mt-4 max-w-xl mx-auto">{restaurant.text}</p>
          <p className="text-sm text-amber-400 mt-3 font-semibold">Open: {restaurant.hours}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search */}
          <div className="max-w-md mx-auto mb-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setActiveCategory(null); }}
              placeholder="Search menu items..."
              className="pl-10 rounded-none border-stone-200 focus-visible:ring-amber-600 h-11"
              data-testid="menu-search-input"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            <button
              onClick={() => { setActiveCategory(null); setSearch(""); }}
              className={`text-[11px] font-bold uppercase tracking-wider px-3 py-2 border transition-colors duration-200 ${
                activeCategory === null && !search ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-stone-200 hover:border-amber-400"
              }`}
              data-testid="menu-all-categories-btn"
            >
              All
            </button>
            {menu.map((cat, i) => (
              <button
                key={i}
                onClick={() => { setActiveCategory(i); setSearch(""); }}
                className={`text-[11px] font-bold uppercase tracking-wider px-3 py-2 border transition-colors duration-200 ${
                  activeCategory === i ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-stone-200 hover:border-amber-400"
                }`}
                data-testid={`menu-category-btn-${i}`}
              >
                {cat.category}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredMenu.map((cat, ci) => (
              <div key={ci} className="bg-white border border-stone-100 p-6" data-testid={`menu-category-${ci}`}>
                <h3 className="text-xl font-bold text-slate-900 mb-1 border-b-2 border-amber-600 pb-3 inline-block" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {cat.category}
                </h3>
                <div className="mt-5 space-y-0">
                  {cat.items.map((item, ii) => (
                    <div
                      key={ii}
                      className="py-2.5 border-b border-stone-50 last:border-0 hover:bg-stone-50 px-1 transition-colors duration-200 group"
                      data-testid={`menu-item-${ci}-${ii}`}
                    >
                      <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors duration-200">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {filteredMenu.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">No menu items found.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
